import { useState } from "react";
import { Link } from "react-router-dom";
import { SignOutButton } from "@clerk/react";
import { toast } from "sonner";
import { ShieldCheck, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useApi, stepUpToken } from "@/lib/api";

const Card = ({ children, testid }) => (
  <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4"><div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8" data-testid={testid}>{children}</div></div>
);

export function StepUp({ me, onDone }) {
  const api = useApi();
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const send = async () => { try { await api("post", "/stepup/start"); setSent(true); toast.success(`Code sent to ${me.email}`); } catch (e) { toast.error(e.message); } };
  const verify = async (e) => {
    e.preventDefault();
    try { const r = await api("post", "/stepup/verify", { code }); stepUpToken.set(r.token); onDone(); } catch (err) { toast.error(err.message); }
  };
  return (
    <Card testid="stepup-card">
      <ShieldCheck className="mb-4 h-8 w-8 text-teal-700" />
      <h1 className="font-display text-2xl font-extrabold">Confirm it's you</h1>
      <p className="mt-2 text-sm text-slate-600">Owners and managers enter a code sent by email each time they sign in.</p>
      {!sent ? <Button className="mt-6 w-full" onClick={send} data-testid="stepup-send-button">Email me a code</Button> : (
        <form onSubmit={verify} className="mt-6 space-y-3">
          <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" data-testid="stepup-code-input" />
          <Button type="submit" className="w-full" disabled={code.length !== 6} data-testid="stepup-verify-button">Continue</Button>
          <button type="button" onClick={send} className="w-full text-sm text-slate-500 underline" data-testid="stepup-resend">Send a new code</button>
        </form>
      )}
      <SignOutButton><button className="mt-6 text-sm text-slate-500" data-testid="stepup-signout">Sign out</button></SignOutButton>
    </Card>
  );
}

export function AcceptGate({ me, onDone }) {
  const api = useApi();
  const [ok, setOk] = useState(false);
  const aup = me.needs_acceptance.includes("aup");
  const submit = async () => {
    try { await api("post", "/legal/accept", { slugs: me.needs_acceptance, method: aup ? "first login" : "re-acceptance" }); onDone(); } catch (e) { toast.error(e.message); }
  };
  return (
    <Card testid="accept-gate">
      <h1 className="font-display text-2xl font-extrabold">{aup ? "Before you upload" : "Updated terms"}</h1>
      {aup ? <p className="mt-3 text-sm text-slate-600">Only upload wholesaler invoices, credit claims and statements. <b>Never upload patient information</b> such as prescriptions, names or NHS numbers. Read the <Link to="/legal/aup" target="_blank" className="font-semibold text-teal-700 underline">Acceptable Use and Upload Policy</Link> and the <Link to="/legal/privacy" target="_blank" className="font-semibold text-teal-700 underline">Privacy Policy</Link>.</p>
        : <p className="mt-3 text-sm text-slate-600">Please review and accept the current <Link to="/legal/terms" target="_blank" className="font-semibold text-teal-700 underline">Terms of Service</Link>. If you don't agree, you can cancel in Settings, Billing.</p>}
      <label className="mt-5 flex items-start gap-3 text-sm"><Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid="accept-checkbox" />
        <span>{aup ? "I have read the Acceptable Use and Upload Policy and will not upload patient information." : "I agree to the Terms of Service, including the Data Processing Agreement and the policies they include."}</span></label>
      <Button className="mt-6 w-full" disabled={!ok} onClick={submit} data-testid="accept-submit">Continue</Button>
      {!aup && <Link to="/settings" className="mt-3 block text-center text-sm text-slate-500 underline" onClick={onDone}>Go to billing instead</Link>}
    </Card>
  );
}

export function AccountGate({ me }) {
  const api = useApi();
  const retry = async () => { try { window.location.href = (await api("post", "/billing/checkout")).checkout_url; } catch (e) { toast.error(e.message); } };
  const msg = { unpaid: "Your subscription hasn't started yet. Add a payment card to finish signing up.", suspended: "This account is suspended. Please contact us.", closed: "This account has closed and its data has been deleted." }[me.access];
  return (
    <Card testid="account-gate">
      <h1 className="font-display text-2xl font-extrabold">{me.org?.name}</h1>
      <p className="mt-3 text-sm text-slate-600" data-testid="account-gate-message">{msg}</p>
      {me.access === "unpaid" && me.role === "owner" && <Button className="mt-6 w-full" onClick={retry} data-testid="account-gate-pay">Add payment card</Button>}
      <SignOutButton><button className="mt-6 text-sm text-slate-500">Sign out</button></SignOutButton>
    </Card>
  );
}

export function NoOrg({ me }) {
  return (
    <Card testid="no-org">
      <h1 className="font-display text-2xl font-extrabold">Welcome</h1>
      <p className="mt-3 text-sm text-slate-600">You're signed in as {me.email}. If your pharmacy already uses InvoiceReconciler.app, ask an owner to invite this email. Or set up your own pharmacy.</p>
      <Link to="/signup" className="mt-6 flex justify-center rounded-full bg-slate-900 px-5 py-3 font-semibold text-white" data-testid="no-org-signup">Set up my pharmacy</Link>
      {me.admin && <Link to="/console" className="mt-3 flex justify-center rounded-full border px-5 py-3 font-semibold" data-testid="no-org-console">Open back office</Link>}
      <SignOutButton><button className="mt-6 text-sm text-slate-500">Sign out</button></SignOutButton>
    </Card>
  );
}

export function Welcome() {
  const items = [["/team", "Invite managers and staff"], ["/wholesalers", "Add your wholesalers"], ["/settings", "Link your pharmacy mailbox"], ["/upload", "Upload your first invoice"], ["/settings", "Choose the automatic photo reading setting"]];
  return (
    <div className="max-w-xl" data-testid="welcome-checklist">
      <h1 className="font-display text-3xl font-extrabold">Welcome checklist</h1>
      <ul className="mt-6 space-y-3">{items.map(([to, t], i) => <li key={t}><Link to={to} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 font-semibold hover:border-slate-900" data-testid={`welcome-item-${i}`}><Check className="h-4 w-4 text-teal-700" />{t}</Link></li>)}</ul>
    </div>
  );
}
